import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { REACTIONS, uuidv7, type Reaction, type TopicStatus } from "@vmeste/shared";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSearchParams } from "react-router-dom";
import { useSession } from "../app/session";
import { useChat, type MessageView, type Sent, type TopicView } from "../app/chat";
import {
  ACCEPT_ATTACHMENTS,
  ALLOWED_MIME,
  MAX_ATTACHMENT_BYTES,
  useAttachments,
} from "../app/attachments.js";
import { AttachmentStrip } from "../components/AttachmentStrip";
import { AddButton, Button, Card } from "../components/ui";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Sheet } from "../components/Sheet";
import { useUnread, type Unread } from "../app/unread";
import { PANE_HEIGHT } from "../app/chrome.js";
import { usePaneFill } from "../app/pane.js";
import {
  ChatIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronUpIcon,
  CloseIcon,
  GripIcon,
  PaperclipIcon,
  PinIcon,
  TrashIcon,
  SendIcon,
  UnpinIcon,
  WarningIcon,
} from "../components/icons";
import { useCollationTag, useDateFormats, useT } from "../i18n/index.js";
import type { DateFormats } from "../i18n/formats.js";
import { cn } from "../lib/cn";


/**
 * Обсуждения — the general chat and the threads (SPEC §4.5).
 *
 * One screen with two modes, because that is what the data is: the space's
 * single `general` topic is a running conversation, and every `thread` is a
 * question with a title and an open/resolved status. Building them as two
 * screens would have meant two composers, two message lists and two sets of
 * edit affordances over identical rows.
 *
 * No "печатает…", no delivery ticks, no per-message read state — SPEC §5 rules
 * all three out, and a chat that fakes them reads as a different product.
 */

/** How close to the bottom still counts as "reading the newest". */
const STICK_THRESHOLD = 64;

/**
 * Today shows a time, anything older shows the day it was said.
 *
 * The formatters are handed in rather than built here: they follow the language
 * the interface is in, which a module-scope `Intl.DateTimeFormat` could not.
 */
function stamp(iso: string, formats: DateFormats): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";

  const now = new Date();
  const sameDay =
    at.getDate() === now.getDate() &&
    at.getMonth() === now.getMonth() &&
    at.getFullYear() === now.getFullYear();

  if (sameDay) return formats.time.format(at);
  if (at.getFullYear() === now.getFullYear()) return formats.dayMonthShort.format(at);
  return formats.dayMonthYearShort.format(at);
}

/** Just the clock. A bubble sits under a day separator, so it never repeats the date. */
function clock(iso: string, formats: DateFormats): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return formats.time.format(at);
}

type Translate = ReturnType<typeof useT>;

/**
 * Bytes in the units a person reads, in their own language.
 *
 * The same shape the strip uses on an uploaded file, over the same message keys:
 * a chip for a file that has not gone up yet has to say its size the way the
 * tile will say it once it has.
 */
function fileSize(bytes: number, t: Translate, tag: string): string {
  if (bytes < 1024) return t("attachments.sizeBytes", { size: bytes });
  if (bytes < 1024 * 1024) {
    return t("attachments.sizeKilobytes", { size: Math.round(bytes / 1024) });
  }
  return t("attachments.sizeMegabytes", {
    size: (bytes / 1024 / 1024).toLocaleString(tag, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }),
  });
}

/** Local midnight for a timestamp, as a comparable number. */
function startOfDay(at: Date): number {
  return new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
}

/**
 * The separator's own words: сегодня, вчера, then the date itself.
 *
 * Yesterday is the previous calendar day rather than "24 hours back" — across a
 * clock change those are not the same day, and the one the separator means is
 * the calendar one.
 */
function dayLabel(iso: string, t: Translate, formats: DateFormats): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";

  const now = new Date();
  const day = startOfDay(at);
  const today = startOfDay(now);
  const yesterday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - 1,
  ).getTime();

  if (day === today) return t("common.today");
  if (day === yesterday) return t("common.yesterday");
  if (at.getFullYear() === now.getFullYear()) return formats.dayMonthLong.format(at);
  return formats.dayMonthYearLong.format(at);
}

/** Whether a message opens a new day, and so needs a separator above it. */
function opensDay(iso: string, previous: string | undefined): boolean {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return false;
  if (previous === undefined) return true;
  const before = new Date(previous);
  if (Number.isNaN(before.getTime())) return true;
  return startOfDay(at) !== startOfDay(before);
}

export function ChatPage() {
  const t = useT();
  const { session } = useSession();
  const spaceId = session?.spaceId ?? null;
  const self = session?.user.id ?? null;

  /*
   * `?topic=` opens one straight away.
   *
   * That is how a discussion about a wish is reached: the wish card links here
   * rather than growing its own message list. Read from the initialiser rather
   * than in an effect, so the conversation is on screen from the first paint
   * instead of flashing the thread list first.
   */
  const [params, setParams] = useSearchParams();
  const requested = params.get("topic");

  const [tab, setTab] = useState<"general" | "threads">(requested ? "threads" : "general");
  const [openThreadId, setOpenThreadId] = useState<string | null>(requested);

  // Spent once. Leaving it in the address would reopen the same discussion on
  // every back-navigation, long after the person moved on from it.
  useEffect(() => {
    if (!requested) return;
    setParams({}, { replace: true });
  }, [requested, setParams]);

  const inThread = tab === "threads" && openThreadId !== null;
  const chat = useChat(spaceId, self, inThread ? openThreadId : null);

  // A thread the other person deleted while it was open. Falling back to the
  // list beats leaving somebody in an empty frame with no way out.
  useEffect(() => {
    if (inThread && chat.ready && chat.topic === null) setOpenThreadId(null);
  }, [inThread, chat.ready, chat.topic]);

  const [adding, setAdding] = useState(false);
  const screenRef = useRef<HTMLDivElement>(null);
  const fill = usePaneFill(screenRef);
  const unread = useUnread(spaceId, self);

  /*
   * An open conversation, seen to the end, is read.
   *
   * Marked from the messages this screen is actually showing rather than from
   * the newest in the space: reading a thread says nothing about the general
   * chat, and marking everything would quietly clear a badge for a
   * conversation nobody opened. `markRead` refuses to move backwards, so
   * switching between two topics cannot undo either.
   */
  useEffect(() => {
    const newest = chat.messages[chat.messages.length - 1];
    if (newest) unread.markRead(newest.id);
  }, [chat.messages, unread]);

  function openThread(topicId: string) {
    setTab("threads");
    setOpenThreadId(topicId);
  }

  return (
    /*
     * No height at all — the browser works it out.
     *
     * This used to be a `calc`: a viewport unit minus a header, minus a bar,
     * minus a safe area, minus a keyboard. Every term was an assumption about
     * how some platform reports something, and two of them were wrong on one
     * phone in ways that took three attempts to tell apart — a gap under the
     * composer, then the composer at the top of the screen.
     *
     * `usePaneFill` measures its way to the answer instead: where this pane
     * starts, and what `<main>` already holds free underneath it. The `calc`
     * beside it covers the one frame before that measurement lands.
     */
    <div ref={screenRef} className="flex flex-col" style={{ height: fill ?? PANE_HEIGHT }}>
      <h1 className="sr-only">{t("chat.title")}</h1>

      {inThread ? (
        <ThreadHeader
          topic={chat.topic}
          onBack={() => setOpenThreadId(null)}
          onRemove={() => {
            if (!chat.topic) return;
            chat.removeTopic(chat.topic.id);
            setOpenThreadId(null);
          }}
          onStatus={(status) => {
            if (chat.topic) chat.setStatus(chat.topic.id, status);
          }}
          onPin={() => {
            if (chat.topic) chat.setPinned(chat.topic.id, !chat.topic.pinned);
          }}
        />
      ) : (
        <TopicTabs
          tab={tab}
          onTab={setTab}
          threadCount={chat.threads.length}
          generalUnread={chat.general ? unread.inTopic(chat.general.id) : 0}
          threadsUnread={chat.threads.reduce(
            (sum, thread) => sum + unread.inTopic(thread.id),
            0,
          )}
          // The «+» belongs to the threads list, so it only appears with it.
          onAdd={tab === "threads" && !adding ? () => setAdding(true) : null}
        />
      )}

      {tab === "threads" && !inThread ? (
        <ThreadList
          threads={chat.threads}
          ready={chat.ready}
          adding={adding}
          unread={unread}
          onOpen={openThread}
          onCloseAdd={() => setAdding(false)}
          onCreate={(title) => {
            setAdding(false);
            const id = chat.createThread(title);
            if (id) openThread(id);
          }}
          onPin={chat.setPinned}
          onMovePinned={chat.movePinned}
        />
      ) : (
        <Conversation
          key={chat.topic?.id ?? "none"}
          spaceId={spaceId}
          topic={chat.topic}
          messages={chat.messages}
          ready={chat.ready}
          onSend={chat.send}
          onEdit={chat.edit}
          onRemove={chat.remove}
          onReact={chat.react}
        />
      )}
    </div>
  );
}

/* --------------------------------------------------------------- switching */

function TopicTabs({
  tab,
  onTab,
  threadCount,
  generalUnread,
  threadsUnread,
  onAdd,
}: {
  tab: "general" | "threads";
  onTab: (tab: "general" | "threads") => void;
  threadCount: number;
  /** New in the general chat, and new across every thread. */
  generalUnread: number;
  threadsUnread: number;
  /** `null` while there is nothing to add here — the general chat, or the form
      already open. */
  onAdd: (() => void) | null;
}) {
  const t = useT();

  const tabs: Array<{ id: "general" | "threads"; label: string; badge: number }> = [
    { id: "general", label: t("chat.general"), badge: generalUnread },
    {
      id: "threads",
      label:
        threadCount > 0
          ? t("chat.threadsCount", { count: threadCount })
          : t("chat.threads"),
      badge: threadsUnread,
    },
  ];

  return (
    <div className="flex shrink-0 items-center gap-2 px-4 pt-3 pb-2">
      {/* Segmented, and a pill inside a pill: the track is a tonal step off the
          ground, the selected segment the one amber thing on the row. Nothing
          here is drawn — the old version lifted the active tab with a shadow,
          which read as a card sitting inside a control. */}
      <div
        role="tablist"
        aria-label={t("chat.viewTabs")}
        className="flex min-w-0 flex-1 gap-1 rounded-pill bg-surface-alt p-1"
      >
        {tabs.map(({ id, label, badge }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => onTab(id)}
            className={cn(
              "flex min-h-[var(--touch)] flex-1 items-center justify-center gap-1.5 rounded-pill px-3 text-[0.9375rem] transition-colors",
              tab === id
                ? "bg-accent-soft font-semibold text-accent-ink"
                : "font-medium text-muted hover:text-ink-soft",
            )}
          >
            <span className="truncate">{label}</span>
            {/* Which of the two halves has something in it. Without this the
                badge in the tab bar says «есть новое» and the screen it opens
                gives no clue where. */}
            {badge > 0 ? (
              <span
                aria-label={t("chat.unreadHere", { count: badge })}
                className="tabular shrink-0 rounded-pill bg-accent px-1.5 text-[0.6875rem] font-bold text-ground"
              >
                {badge > 99 ? "99+" : badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {onAdd ? <AddButton label={t("chat.newThreadLabel")} onClick={onAdd} /> : null}
    </div>
  );
}

function ThreadHeader({
  topic,
  onBack,
  onStatus,
  onPin,
  onRemove,
}: {
  topic: TopicView | null;
  onBack: () => void;
  onStatus: (status: TopicStatus) => void;
  onPin: () => void;
  onRemove: () => void;
}) {
  const t = useT();
  const resolved = topic?.status === "resolved";
  const pinned = topic?.pinned ?? false;
  const [asking, setAsking] = useState(false);

  return (
    // No rule under the header: the conversation below it sits on the ground and
    // the header is part of the same ground, so a line here would be the one
    // drawn edge on the screen.
    <div className="flex shrink-0 items-center gap-1.5 px-2 py-2">
      <button
        type="button"
        onClick={onBack}
        aria-label={t("chat.backToThreads")}
        className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-surface hover:text-ink"
      >
        <ChevronLeftIcon className="size-5" strokeWidth={1.9} />
      </button>

      <div className="min-w-0 flex-1">
        <h2 className="truncate font-display text-lg leading-tight">
          {topic?.title ?? t("chat.threadFallback")}
        </h2>
        <p className="text-[0.75rem] text-muted">
          {resolved ? t("chat.statusResolved") : t("chat.statusOpen")} ·{" "}
          {t("chat.messageCount", { count: topic?.messageCount ?? 0 })}
        </p>
      </div>

      {/* Here as well as in the list, because most discussions are reached from
          the thing they are about — a wish, an event — and never pass the list
          at all. Amber when it is pinned: the state is the colour, not a badge. */}
      {topic ? (
        <button
          type="button"
          onClick={onPin}
          aria-pressed={pinned}
          aria-label={t(pinned ? "chat.unpin" : "chat.pin", { title: topic.title })}
          className={cn(
            "grid size-[var(--touch)] shrink-0 place-items-center rounded-pill transition-colors",
            pinned
              ? "text-accent hover:bg-accent-soft"
              : "text-muted hover:bg-surface hover:text-ink",
          )}
        >
          {pinned ? (
            <UnpinIcon className="size-[18px]" strokeWidth={1.8} />
          ) : (
            <PinIcon className="size-[18px]" strokeWidth={1.8} />
          )}
        </button>
      ) : null}

      {/* Threads are not all typed by hand — every discussion of a wish, an
          item or an event makes one — so a thumb landing on «Обсудить» by
          mistake used to leave a thread that could only ever be marked
          «решена». Never the general chat: it is the space's whole history,
          and the server refuses it too. */}
      {topic && topic.kind !== "general" ? (
        <button
          type="button"
          onClick={() => setAsking(true)}
          aria-label={t("chat.removeThread", { title: topic.title })}
          className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-neg-soft hover:text-neg"
        >
          <TrashIcon className="size-[18px]" strokeWidth={1.8} />
        </button>
      ) : null}

      <Button
        variant={resolved ? "ghost" : "secondary"}
        onClick={() => onStatus(resolved ? "open" : "resolved")}
        className="shrink-0 px-3 text-[0.8125rem]"
      >
        {resolved ? null : <CheckIcon className="size-[18px]" strokeWidth={1.9} />}
        {resolved ? t("chat.reopen") : t("chat.resolve")}
      </Button>

      {asking && topic ? (
        <ConfirmDialog
          title={t("chat.removeThreadTitle", { title: topic.title })}
          confirmLabel={t("common.delete")}
          onCancel={() => setAsking(false)}
          onConfirm={() => {
            setAsking(false);
            onRemove();
          }}
        >
          <p>{t("chat.removeThreadWhat", { count: topic.messageCount })}</p>
          <p className="text-neg">{t("chat.removeThreadBoth")}</p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ threads */

/** The one heading shape this screen uses above a group of threads. */
function GroupHeading({ children }: { children: string }) {
  return (
    <h2 className="px-1 pt-1 text-[0.8125rem] font-semibold tracking-wide text-muted uppercase">
      {children}
    </h2>
  );
}

function ThreadList({
  threads,
  ready,
  adding,
  unread,
  onOpen,
  onCreate,
  onCloseAdd,
  onPin,
  onMovePinned,
}: {
  threads: TopicView[];
  ready: boolean;
  /** Held above, because the «+» that opens the form sits beside the tabs. */
  adding: boolean;
  unread: Unread;
  onOpen: (topicId: string) => void;
  onCreate: (title: string) => void;
  onCloseAdd: () => void;
  onPin: (topicId: string, pinned: boolean) => void;
  onMovePinned: (topicId: string, toIndex: number) => void;
}) {
  const t = useT();

  /*
   * Pinned first, and a pinned thread leaves the open/resolved groups entirely.
   *
   * Threads are not all typed by hand — discussing a wish, an item or an event
   * makes one — so the list grows on its own and «решена» is a weaker signal
   * than «мы это держим наверху». A pin that stayed in its status group would be
   * a pin that does not pin.
   *
   * The order inside the group is the store's, not this file's.
   */
  const pinned = threads.filter((topic) => topic.pinned);
  const rest = threads.filter((topic) => !topic.pinned);
  const open = rest.filter((topic) => topic.status === "open");
  const resolved = rest.filter((topic) => topic.status === "resolved");

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain px-4 pb-4">
      {/* Opened by the «+» beside the tabs. It used to stand here always — a
          field waiting at the top of a list most visits only read. */}
      {adding ? <NewThread onCreate={onCreate} onClose={onCloseAdd} /> : null}

      {threads.length === 0 ? (
        <Card>
          <p className="px-4 py-6 text-center text-[0.9375rem] text-muted">
            {ready ? t("chat.threadsEmpty") : t("chat.threadsLoading")}
          </p>
        </Card>
      ) : null}

      {pinned.length > 0 ? (
        <>
          <GroupHeading>{t("chat.pinnedHeading")}</GroupHeading>
          <PinnedThreads
            pinned={pinned}
            onOpen={onOpen}
            onUnpin={(topicId) => onPin(topicId, false)}
            onMove={onMovePinned}
          />
        </>
      ) : null}

      {open.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {open.map((topic) => (
            <ThreadRow
              key={topic.id}
              topic={topic}
              unread={unread.inTopic(topic.id)}
              onOpen={() => onOpen(topic.id)}
              onPin={() => onPin(topic.id, true)}
            />
          ))}
        </ul>
      ) : null}

      {resolved.length > 0 ? (
        <>
          <GroupHeading>{t("chat.resolvedHeading")}</GroupHeading>
          <ul className="flex flex-col gap-2">
            {resolved.map((topic) => (
              <ThreadRow
                key={topic.id}
                topic={topic}
                unread={unread.inTopic(topic.id)}
                onOpen={() => onOpen(topic.id)}
                onPin={() => onPin(topic.id, true)}
              />
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

/**
 * The pinned group: one surface of compact rows, not a stack of cards.
 *
 * Cards would say "these are more of the same, higher up"; a single held-still
 * shelf says "these are the ones we keep". It is also what makes reordering read
 * as one gesture over one object rather than as dragging cards around a page.
 */
function PinnedThreads({
  pinned,
  onOpen,
  onUnpin,
  onMove,
}: {
  pinned: TopicView[];
  onOpen: (topicId: string) => void;
  onUnpin: (topicId: string) => void;
  onMove: (topicId: string, toIndex: number) => void;
}) {
  const sensors = useSensors(
    // A few pixels of travel before a drag starts, or tapping a row on a phone
    // would be read as the beginning of one.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const to = pinned.findIndex((topic) => topic.id === over.id);
    if (to === -1) return;

    onMove(String(active.id), to);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={onDragEnd}
    >
      <Card>
        <ul className="divide-y divide-line-soft">
          <SortableContext
            items={pinned.map((topic) => topic.id)}
            strategy={verticalListSortingStrategy}
          >
            {pinned.map((topic, index) => (
              <PinnedRow
                key={topic.id}
                topic={topic}
                first={index === 0}
                last={index === pinned.length - 1}
                onOpen={() => onOpen(topic.id)}
                onUnpin={() => onUnpin(topic.id)}
                onUp={() => onMove(topic.id, index - 1)}
                onDown={() => onMove(topic.id, index + 1)}
              />
            ))}
          </SortableContext>
        </ul>
      </Card>
    </DndContext>
  );
}

/**
 * One pinned thread, with two ways to move it.
 *
 * The grip is the drag, and the arrows are there because the main screen is a
 * phone: this list lives inside a pane that scrolls, and a drag that begins on a
 * scrolling surface is a fight between two gestures at the best of times. Two
 * taps are not the elegant answer, they are the one that works with a thumb.
 */
function PinnedRow({
  topic,
  first,
  last,
  onOpen,
  onUnpin,
  onUp,
  onDown,
}: {
  topic: TopicView;
  first: boolean;
  last: boolean;
  onOpen: () => void;
  onUnpin: () => void;
  onUp: () => void;
  onDown: () => void;
}) {
  const t = useT();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: topic.id });

  // Narrow and tall: the touch floor is a height, so a column of controls can be
  // thin without any of them being small to hit.
  const nudge =
    "grid h-[var(--touch)] w-7 shrink-0 place-items-center rounded-pill text-muted transition-colors hover:text-ink disabled:text-line";

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        "group flex items-center gap-0.5 bg-surface py-1 pr-1 pl-1.5",
        isDragging && "relative z-10 shadow-soft",
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={t("chat.movePinned", { title: topic.title })}
        className="grid h-[var(--touch)] w-6 shrink-0 touch-none cursor-grab place-items-center rounded-pill text-line transition-colors group-hover:text-muted active:cursor-grabbing"
      >
        <GripIcon className="size-[18px]" strokeWidth={1.8} />
      </button>

      <button
        type="button"
        onClick={onOpen}
        className={cn(
          "flex min-h-[var(--touch)] min-w-0 flex-1 flex-col justify-center rounded-row px-1 text-left",
          // A settled thread that is still held here reads the same way it does
          // in the list below — quieter, not gone. The controls keep their
          // contrast, because they are still fully usable.
          topic.status === "resolved" && "opacity-70",
        )}
      >
        <span className="truncate">{topic.title}</span>
        <span className="truncate text-[0.75rem] text-muted">
          {topic.lastMessage
            ? `${topic.lastMessage.authorName}: ${topic.lastMessage.body}`
            : t("chat.noReplies")}
        </span>
      </button>

      <button
        type="button"
        disabled={first}
        onClick={onUp}
        aria-label={t("chat.moveUp", { title: topic.title })}
        className={nudge}
      >
        <ChevronUpIcon className="size-[18px]" strokeWidth={1.9} />
      </button>
      <button
        type="button"
        disabled={last}
        onClick={onDown}
        aria-label={t("chat.moveDown", { title: topic.title })}
        className={nudge}
      >
        <ChevronDownIcon className="size-[18px]" strokeWidth={1.9} />
      </button>

      <button
        type="button"
        onClick={onUnpin}
        aria-label={t("chat.unpin", { title: topic.title })}
        className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-accent transition-colors hover:bg-accent-soft"
      >
        <UnpinIcon className="size-[18px]" strokeWidth={1.8} />
      </button>
    </li>
  );
}

/**
 * Title first, conversation second.
 *
 * A thread exists because somebody named a question, so naming it is the whole
 * of the create step; the first message comes once it is open.
 */
function NewThread({
  onCreate,
  onClose,
}: {
  onCreate: (title: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [title, setTitle] = useState("");

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) return;
    onCreate(title);
    setTitle("");
    onClose();
  }

  return (
    <Sheet title={t("chat.newThreadLabel")} onClose={onClose}>
      <form onSubmit={submit} className="flex items-center gap-2 p-4">
        <input
          autoFocus
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t("chat.newThreadPlaceholder")}
          aria-label={t("chat.newThreadLabel")}
          className="min-h-[var(--touch)] min-w-0 flex-1 rounded-row bg-surface-alt px-3 outline-none placeholder:text-muted"
        />
        <Button
          type="submit"
          disabled={!title.trim()}
          className="shrink-0 px-3 text-[0.8125rem]"
        >
          {t("chat.newThreadSubmit")}
        </Button>
      </form>
    </Sheet>
  );
}

function ThreadRow({
  topic,
  unread,
  onOpen,
  onPin,
}: {
  topic: TopicView;
  /** New since this person last looked. Nought draws nothing. */
  unread: number;
  onOpen: () => void;
  onPin: () => void;
}) {
  const t = useT();
  const formats = useDateFormats();
  const resolved = topic.status === "resolved";

  return (
    <li>
      {/* The card is the row, and the pin is a second control beside the one that
          opens it — nested inside, it would be a button within a button, which
          no browser and no screen reader agrees about. */}
      <Card className={cn(resolved && "opacity-70")}>
        <div className="flex items-start">
          <button
            type="button"
            onClick={onOpen}
            className="flex min-w-0 flex-1 flex-col gap-1 px-4 py-3 text-left"
          >
            <span className="flex w-full items-start gap-2">
              <span
                className={cn("min-w-0 flex-1 break-words", unread > 0 && "font-semibold")}
              >
                {topic.title}
              </span>
              {/* «5 сообщений» said how many there are, which is not the
                  question anybody opens this list with. */}
              {unread > 0 ? (
                <span
                  aria-label={t("chat.unreadHere", { count: unread })}
                  className="tabular shrink-0 rounded-pill bg-accent px-2 py-0.5 text-[0.75rem] font-bold text-ground"
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              ) : null}
              {resolved ? (
                <span className="shrink-0 rounded-pill bg-accent-soft px-2.5 py-0.5 text-[0.75rem] text-accent-ink">
                  {t("chat.statusResolved")}
                </span>
              ) : null}
            </span>

            {topic.lastMessage ? (
              <span className="w-full truncate text-[0.8125rem] text-muted">
                <span className="text-ink-soft">{topic.lastMessage.authorName}:</span>{" "}
                {topic.lastMessage.body}
              </span>
            ) : (
              <span className="text-[0.8125rem] text-muted">{t("chat.noReplies")}</span>
            )}

            <span className="text-[0.75rem] text-muted">
              {t("chat.messageCount", { count: topic.messageCount })} ·{" "}
              <span className="tabular">{stamp(topic.updatedAt, formats)}</span>
            </span>
          </button>

          <button
            type="button"
            onClick={onPin}
            aria-label={t("chat.pin", { title: topic.title })}
            className="m-1.5 grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-surface-alt hover:text-ink"
          >
            <PinIcon className="size-[18px]" strokeWidth={1.8} />
          </button>
        </div>
      </Card>
    </li>
  );
}

/* ------------------------------------------------------------ conversation */

function Conversation({
  spaceId,
  topic,
  messages,
  ready,
  onSend,
  onEdit,
  onRemove,
  onReact,
}: {
  spaceId: string | null;
  topic: TopicView | null;
  messages: MessageView[];
  ready: boolean;
  onSend: (input: { body: string; replyToId?: string | null }) => Promise<Sent | null>;
  onEdit: (messageId: string, body: string) => void;
  onRemove: (messageId: string) => void;
  onReact: (messageId: string, emoji: Reaction) => void;
}) {
  const t = useT();
  const formats = useDateFormats();
  const paneRef = useRef<HTMLDivElement | null>(null);
  /** Whether the view is following the newest message. A ref, because the
      scroll handler and the arrival effect both read it in the same tick. */
  const following = useRef(true);

  const [atBottom, setAtBottom] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<MessageView | null>(null);
  const [editing, setEditing] = useState<MessageView | null>(null);

  const count = messages.length;
  const lastId = messages[count - 1]?.id ?? null;

  // Opening a conversation lands on the newest message, which is where reading
  // starts.
  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return;
    pane.scrollTop = pane.scrollHeight;
    following.current = true;
    setAtBottom(true);
  }, []);

  /*
   * Stay at the bottom while the conversation is still growing under it.
   *
   * Scrolling once, on arrival, was not enough: photographs decrypt and lay out
   * after the fact, fonts settle, day dividers appear — and every one of those
   * adds height *below* the place the view was put. Opening a chat therefore
   * landed somewhere above the newest message, which reads as the app having
   * chosen a different day to show you.
   *
   * The observer is the only honest answer, because "when has it finished
   * growing" has no other signal. It re-pins only while `following` is true, so
   * somebody reading history is never yanked.
   */
  useEffect(() => {
    const pane = paneRef.current;
    if (!pane || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      if (!following.current) return;
      pane.scrollTop = pane.scrollHeight;
    });

    // The pane itself for a keyboard opening, and its content for everything
    // that arrives inside it.
    observer.observe(pane);
    for (const child of pane.children) observer.observe(child);

    return () => observer.disconnect();
  }, [topic?.id]);

  /**
   * A new message pulls the view down only if the view was already down there.
   *
   * Yanking somebody out of the history they are reading is the one thing a
   * chat must not do, so this checks before it scrolls rather than after.
   */
  useEffect(() => {
    const pane = paneRef.current;
    if (!pane || !following.current) return;
    pane.scrollTop = pane.scrollHeight;
  }, [lastId, count]);

  function handleScroll() {
    const pane = paneRef.current;
    if (!pane) return;
    const distance = pane.scrollHeight - pane.scrollTop - pane.clientHeight;
    following.current = distance < STICK_THRESHOLD;
    setAtBottom(following.current);
  }

  function scrollToNewest() {
    const pane = paneRef.current;
    if (!pane) return;
    pane.scrollTo({ top: pane.scrollHeight, behavior: "smooth" });
    following.current = true;
    setAtBottom(true);
  }

  /**
   * Hand the words to the outbox and the id back to the composer.
   *
   * The composer is what does the uploading, because it is what holds the
   * previews: a file and the percentage on top of it are one object on screen,
   * and splitting them across two components meant nobody owned the moment an
   * upload failed. All this side has to do is answer with the id the message
   * was minted under and a promise saying whether it ever arrived — or `null`,
   * which the composer says out loud.
   */
  function send(text: string): Promise<Sent | null> {
    const replyToId = replyTo?.id ?? null;
    setReplyTo(null);
    setSelectedId(null);
    // Your own message always brings you back down: you just wrote it.
    following.current = true;
    setAtBottom(true);
    return onSend({ body: text, replyToId });
  }

  function saveEdit(text: string) {
    if (!editing) return;
    onEdit(editing.id, text);
    setEditing(null);
    setSelectedId(null);
  }

  return (
    <>
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={paneRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto overscroll-contain px-4"
        >
          {count === 0 ? (
            <EmptyConversation topic={topic} ready={ready} />
          ) : (
            <ol className="flex flex-col gap-1.5 py-4">
              {messages.map((message, index) => (
                <Fragment key={message.id}>
                  {opensDay(message.createdAt, messages[index - 1]?.createdAt) ? (
                    <DayDivider label={dayLabel(message.createdAt, t, formats)} />
                  ) : null}
                  <MessageRow
                    spaceId={spaceId}
                    message={message}
                    selected={selectedId === message.id}
                    onSelect={() =>
                      setSelectedId((current) =>
                        current === message.id ? null : message.id,
                      )
                    }
                    onReply={() => {
                      setEditing(null);
                      setReplyTo(message);
                      setSelectedId(null);
                    }}
                    onEdit={() => {
                      setReplyTo(null);
                      setEditing(message);
                      setSelectedId(null);
                    }}
                    onRemove={() => {
                      onRemove(message.id);
                      setSelectedId(null);
                      if (editing?.id === message.id) setEditing(null);
                      if (replyTo?.id === message.id) setReplyTo(null);
                    }}
                    onReact={(emoji) => {
                      onReact(message.id, emoji);
                      // Closed after the choice: the palette is a menu, and a
                      // menu that stays open has to be dismissed by hand.
                      setSelectedId(null);
                    }}
                  />
                </Fragment>
              ))}
            </ol>
          )}
        </div>

        {!atBottom && count > 0 ? (
          <button
            type="button"
            onClick={scrollToNewest}
            aria-label={t("chat.toNewest")}
            className="absolute right-4 bottom-3 grid size-[var(--touch)] place-items-center rounded-pill bg-surface-alt text-ink-soft shadow-soft"
          >
            <ChevronDownIcon className="size-5" strokeWidth={1.9} />
          </button>
        ) : null}
      </div>

      <Composer
        spaceId={spaceId}
        disabled={topic === null}
        replyTo={replyTo}
        editing={editing}
        onSend={send}
        onSaveEdit={saveEdit}
        onCancelReply={() => setReplyTo(null)}
        onCancelEdit={() => setEditing(null)}
      />
    </>
  );
}

function EmptyConversation({
  topic,
  ready,
}: {
  topic: TopicView | null;
  ready: boolean;
}) {
  const t = useT();

  if (!ready) {
    return (
      <p className="py-16 text-center text-[0.9375rem] text-muted">
        {t("chat.loading")}
      </p>
    );
  }

  // No general topic means the server has not made one for this space. Saying
  // so is better than a composer that silently swallows what you type.
  if (!topic) {
    return (
      <p className="py-16 text-center text-[0.9375rem] text-muted">
        {t("chat.noGeneral")}
      </p>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
      <ChatIcon className="size-[22px] text-muted" strokeWidth={1.8} />
      <p className="text-[0.9375rem] text-muted">
        {topic.kind === "general" ? t("chat.generalEmpty") : t("chat.threadEmpty")}
      </p>
    </div>
  );
}

/**
 * Where one day ends.
 *
 * A pill rather than a rule with words in the middle: the line through the
 * middle is the drawn separator «Тихо» does not have, and a floating pill one
 * tonal step off the ground says the same thing without it.
 */
function DayDivider({ label }: { label: string }) {
  return (
    <li className="flex justify-center py-2">
      <span className="rounded-pill bg-surface px-3 py-1 text-[0.75rem] text-muted">
        {label}
      </span>
    </li>
  );
}

/**
 * One message.
 *
 * Tapping a bubble opens its actions rather than revealing them on hover: a
 * phone has no hover, and a row of always-visible buttons under every message
 * would drown the conversation.
 */
function MessageRow({
  spaceId,
  message,
  selected,
  onSelect,
  onReply,
  onEdit,
  onRemove,
  onReact,
}: {
  spaceId: string | null;
  message: MessageView;
  selected: boolean;
  onSelect: () => void;
  onReply: () => void;
  onEdit: () => void;
  onRemove: () => void;
  onReact: (emoji: Reaction) => void;
}) {
  const t = useT();
  const formats = useDateFormats();
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!selected) setConfirming(false);
  }, [selected]);

  /*
   * A message that is only a photograph, which is most photographs.
   *
   * It used to draw the bubble anyway, and with nothing to put in it the bubble
   * came out as an amber tab holding «Вы 09:38» floating above an unrelated
   * picture — two objects where the person sent one. The composer refuses a
   * message that is neither words nor files, so an empty body here *is* the
   * picture; the bubble has nothing left to be.
   *
   * A quoted reply keeps the bubble even without words: the quote is something
   * to read, and it has to sit in something.
   */
  const wordless = !message.body && !message.replyTo;

  const meta = (
    <>
      <span>{message.mine ? t("common.you") : message.authorName}</span>
      <span className="tabular">{clock(message.createdAt, formats)}</span>
      {/* Quietly, and only as a fact — not as a badge. */}
      {message.edited ? <span>{t("chat.edited")}</span> : null}
    </>
  );

  const faces =
    message.reactions.length > 0 ? (
      <div
        className={cn(
          "mt-1 flex flex-wrap gap-1",
          message.mine ? "justify-end" : "justify-start",
        )}
      >
        {message.reactions.map((reaction) => (
          <button
            key={reaction.userId}
            type="button"
            onClick={() => onReact(reaction.emoji)}
            // Only yours does anything: tapping somebody else's face would
            // either do nothing (a button that lies) or copy it (a gesture
            // nobody asked for).
            disabled={!reaction.mine}
            aria-label={
              reaction.mine
                ? t("chat.reactionYours", { emoji: reaction.emoji })
                : t("chat.reactionTheirs", {
                    emoji: reaction.emoji,
                    name: reaction.authorName,
                  })
            }
            className={cn(
              "flex items-center gap-1 rounded-pill px-2 py-0.5 text-[0.8125rem] leading-none",
              reaction.mine ? "bg-accent-soft text-accent-ink" : "bg-surface-alt text-ink-soft",
            )}
          >
            <span aria-hidden="true" className="text-[0.9375rem]">
              {reaction.emoji}
            </span>
          </button>
        ))}
      </div>
    ) : null;

  const actions = selected ? (
    <>
      <div
        className={cn(
          "mt-1 flex flex-wrap gap-1",
          message.mine ? "justify-end" : "justify-start",
        )}
      >
        {/* Six, in the order they are declared, and every one a full touch
            target — this row is tapped with a thumb on a moving train. */}
        {REACTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onReact(emoji)}
            aria-label={t("chat.react", { emoji })}
            aria-pressed={message.myReaction === emoji}
            className={cn(
              "grid size-[var(--touch)] place-items-center rounded-pill text-[1.25rem] transition-colors",
              message.myReaction === emoji ? "bg-accent-soft" : "hover:bg-surface-alt",
            )}
          >
            <span aria-hidden="true">{emoji}</span>
          </button>
        ))}
      </div>

      <div
        className={cn(
          "mt-1 flex flex-wrap gap-1",
          message.mine ? "justify-end" : "justify-start",
        )}
      >
        <Button variant="ghost" onClick={onReply} className="px-3 text-[0.8125rem]">
          {t("chat.reply")}
        </Button>

        {/* Somebody else's message offers neither of these. */}
        {message.mine ? (
          <>
            <Button variant="ghost" onClick={onEdit} className="px-3 text-[0.8125rem]">
              {t("common.edit")}
            </Button>
            <Button
              variant="ghost"
              onClick={() => (confirming ? onRemove() : setConfirming(true))}
              className={cn("px-3 text-[0.8125rem]", confirming && "text-neg")}
            >
              {confirming ? t("common.confirmDelete") : t("common.delete")}
            </Button>
          </>
        ) : null}
      </div>
    </>
  ) : null;

  if (wordless) {
    return (
      <li className={cn("flex flex-col", message.mine ? "items-end" : "items-start")}>
        <div
          className={cn(
            "max-w-[85%] rounded-card transition-shadow",
            selected && (message.mine ? "ring-2 ring-ground/45" : "ring-2 ring-accent"),
          )}
        >
          <AttachmentStrip
            spaceId={spaceId}
            target={{ type: "message", id: message.id }}
            readOnly
            wide
          />

          {/*
            The line under the picture is what selects the message.
            
            The picture itself cannot be: tapping it opens the viewer, which is
            the thing anybody taps a photograph for. So the author and the hour
            do double duty — they were going to be drawn anyway, and they are
            the only part of this message that is not the photograph.
          */}
          <button
            type="button"
            onClick={onSelect}
            aria-expanded={selected}
            className={cn(
              "flex w-full flex-wrap items-center gap-x-1.5 rounded-b-card px-2 py-1.5 text-[0.6875rem] text-muted",
              message.mine ? "justify-end" : "justify-start",
            )}
          >
            {meta}
          </button>
        </div>

        {faces}
        {actions}
      </li>
    );
  }

  return (
    <li className={cn("flex flex-col", message.mine ? "items-end" : "items-start")}>
      {/*
        No tail, and no border on either side.

        The bubble is 22px all round except the bottom corner on its own side,
        which drops to 8px — that notch is what points the bubble at its sender,
        and it does the job the drawn tail used to. Mine is the filled amber, so
        everything inside it has to be a tint of `ground` rather than of `muted`:
        muted grey on amber is nearly invisible in both themes.
      */}
      <button
        type="button"
        onClick={onSelect}
        aria-expanded={selected}
        className={cn(
          "max-w-[85%] rounded-card px-3.5 py-2 text-left transition-colors",
          message.mine
            ? "rounded-br-[8px] bg-accent text-ground"
            : "rounded-bl-[8px] bg-surface text-ink",
          selected && (message.mine ? "ring-2 ring-ground/45" : "ring-2 ring-accent"),
        )}
      >
        {message.replyTo ? (
          <span className="mb-1.5 flex gap-2 text-left">
            <span
              aria-hidden="true"
              className={cn(
                "w-0.5 shrink-0 rounded-pill",
                message.mine ? "bg-ground/40" : "bg-accent",
              )}
            />
            <span className="min-w-0">
              <span
                className={cn(
                  "block text-[0.75rem] font-medium",
                  message.mine ? "text-ground/70" : "text-accent",
                )}
              >
                {message.replyTo.authorName}
              </span>
              <span
                className={cn(
                  "line-clamp-2 block text-[0.8125rem]",
                  message.mine ? "text-ground/60" : "text-muted",
                )}
              >
                {message.replyTo.excerpt}
              </span>
            </span>
          </span>
        ) : null}

        {/* Empty only when a quote is standing in for the words — a wordless
            message with nothing to quote never reaches this branch. */}
        {message.body ? (
          <span className="block break-words whitespace-pre-wrap">{message.body}</span>
        ) : null}

        <span
          className={cn(
            "mt-1 flex flex-wrap items-center gap-x-1.5 text-[0.6875rem]",
            message.mine ? "text-ground/60" : "text-muted",
          )}
        >
          {meta}
        </span>
      </button>

      {/* Under the bubble, not inside it: a photo is a thing said alongside the
          words, and the strip is the same row of tiles it is everywhere else.
          It renders nothing at all when this message has no files, so a plain
          conversation looks exactly as it did. */}
      <AttachmentStrip
        spaceId={spaceId}
        target={{ type: "message", id: message.id }}
        readOnly
        // A photograph here is the message, not a footnote to it.
        wide
        className="mt-1 max-w-[85%]"
      />

      {faces}
      {actions}
    </li>
  );
}

/* -------------------------------------------------------------- composing */

/** A file chosen in the composer: before it goes, while it goes, or stuck. */
interface Picked {
  /**
   * Ours from the moment of picking, and handed to `upload` as the id of its
   * row — which is how the percentage finds its way back to this tile.
   */
  id: string;
  file: File;
  /** An object URL for a picture, and `null` for everything else. Revoked below. */
  url: string | null;
  /** Refused when it was chosen, or refused on the way up. */
  error: string | null;
  /** Gone with a message: no longer removable, and its progress is real. */
  sent: boolean;
}

/**
 * Why this file cannot be sent, asked the moment it is chosen.
 *
 * The same two rules the upload enforces, over the same two constants — but
 * asked *here*, before a message has been sent, because after it the answer
 * arrives attached to a conversation that has already moved on. The picker is
 * deliberately wider than the allow-list (an iPhone's photographs are HEIC), so
 * something does have to say no, and this is the only moment at which saying no
 * costs nobody anything.
 */
function refuse(file: File, t: Translate): string | null {
  const mime = file.type.split(";")[0]?.trim().toLowerCase() ?? "";
  if (!(ALLOWED_MIME as readonly string[]).includes(mime)) {
    return t("attachments.errorUnsupported");
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return t("attachments.errorTooLarge", {
      size: Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024),
    });
  }
  if (file.size === 0) return t("attachments.errorEmptyFile");
  return null;
}

/**
 * One file as the composer shows it: the picture itself, big enough to check.
 *
 * A chip with a filename on it answers "did the tap register"; it does not
 * answer "is this the right photograph", which is the question somebody about
 * to send one is actually asking. So the picture is the tile, the percentage
 * sits on top of the picture rather than beside it, and a refusal replaces
 * both with the reason.
 */
function Preview({
  item,
  solo,
  progress,
  onRemove,
}: {
  item: Picked;
  /** The only one picked, and so given the room to be looked at properly. */
  solo: boolean;
  /** 0…1, the real fraction of bytes on the wire. */
  progress: number;
  onRemove: () => void;
}) {
  const t = useT();
  const tag = useCollationTag();

  const frame = cn(
    "relative overflow-hidden rounded-card bg-surface-alt",
    // No border: the tonal step off the ground is the edge. One picture gets a
    // proper look at it; more than one and they become squares that scroll.
    solo ? "h-44 w-60 max-w-[70vw]" : "size-32",
  );

  if (item.error) {
    return (
      <button
        type="button"
        onClick={onRemove}
        aria-label={t("attachments.dismissError", { error: item.error })}
        className={cn(
          frame,
          "flex flex-col items-center justify-center gap-1.5 bg-neg-soft px-3 text-center",
        )}
      >
        <WarningIcon className="size-5 shrink-0 text-neg" strokeWidth={1.8} />
        {/* The one place on the screen that says what went wrong, so it is the
            one place that announces itself. */}
        <span role="alert" className="line-clamp-3 text-[0.8125rem] leading-tight text-neg">
          {item.error}
        </span>
        <span className="text-[0.6875rem] text-neg/80">{t("attachments.tapToDismiss")}</span>
      </button>
    );
  }

  const percent = Math.round(progress * 100);

  return (
    <div className={frame}>
      {item.url ? (
        <img src={item.url} alt={item.file.name} className="size-full object-cover" />
      ) : (
        <div className="flex size-full flex-col items-center justify-center gap-1.5 px-3 text-center">
          <PaperclipIcon className="size-5 shrink-0 text-muted" strokeWidth={1.8} />
          <span className="line-clamp-2 text-[0.8125rem] leading-tight text-ink-soft">
            {item.file.name}
          </span>
          <span className="tabular text-[0.6875rem] text-muted">
            {fileSize(item.file.size, t, tag)}
          </span>
        </div>
      )}

      {item.sent ? (
        // The picture dims and the number sits in the middle of it. `ground` is
        // the page's own colour in both themes, so the scrim darkens in the dark
        // one and lightens in the light one without a single `dark:` variant.
        <div
          aria-busy="true"
          aria-label={t("attachments.uploading", { name: item.file.name, percent })}
          className="absolute inset-0 grid place-items-center bg-ground/70"
        >
          <span className="tabular text-lg font-semibold text-ink">{percent}%</span>
          <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1 bg-line-soft">
            <span
              className="block h-full bg-accent transition-[width] duration-200"
              style={{ width: `${percent}%` }}
            />
          </span>
        </div>
      ) : (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t("attachments.unpick", { name: item.file.name })}
          // The full touch target, with the visible circle in its corner: the
          // tap area clears 44px without covering the photograph.
          className="absolute top-0 right-0 grid size-[var(--touch)] place-items-start justify-end p-1.5"
        >
          <span className="grid size-7 place-items-center rounded-pill bg-ground/75 text-ink transition-colors hover:text-neg">
            <CloseIcon className="size-4" strokeWidth={2} />
          </span>
        </button>
      )}
    </div>
  );
}

/**
 * The composer, and the one place text is written.
 *
 * Replying and editing reuse it rather than opening a second box: there is only
 * ever one thing being written, and what it is doing is stated above it.
 *
 * It also owns the files, from the moment they are picked to the moment they
 * are up. That is a deliberate move: the previews live here, the percentages
 * belong on the previews, and a failure that nothing on screen owned was how a
 * refused file came to disappear without a word.
 */
function Composer({
  spaceId,
  disabled,
  replyTo,
  editing,
  onSend,
  onSaveEdit,
  onCancelReply,
  onCancelEdit,
}: {
  spaceId: string | null;
  disabled: boolean;
  replyTo: MessageView | null;
  editing: MessageView | null;
  /** The id the message was minted under, and whether it arrives. */
  onSend: (text: string) => Promise<Sent | null>;
  onSaveEdit: (text: string) => void;
  onCancelReply: () => void;
  onCancelEdit: () => void;
}) {
  const t = useT();
  /**
   * Files chosen here, and kept until they are actually up.
   *
   * Not an `AttachmentStrip`: that strip lists what is attached to something,
   * and until the message has been sent there is nothing to attach these to.
   */
  const [picked, setPicked] = useState<Picked[]>([]);
  const [text, setText] = useState("");
  /*
   * No target, and that is the point: a message's id exists only after it has
   * been sent, so the rows these uploads write cannot be asked for by target
   * from here. Without a target the hook answers with every upload in the
   * space, and each tile finds its own by the id it handed to `upload`.
   */
  const { keep, sendKept, forget, pending, dismiss } = useAttachments(spaceId);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  /*
   * Every object URL made here is handed back.
   *
   * A revoked URL is the difference between a preview and a photograph the tab
   * holds until it is closed — on a phone, three of those is real memory. The
   * ref is what the unmount cleanup reads: leaving the tab or opening another
   * thread has to release them too, not just tapping ×.
   */
  const pickedRef = useRef<Picked[]>([]);
  useEffect(() => {
    pickedRef.current = picked;
  }, [picked]);
  useEffect(() => {
    return () => {
      for (const row of pickedRef.current) if (row.url) URL.revokeObjectURL(row.url);
    };
  }, []);

  /** Drop one tile and release its picture. */
  function drop(item: Picked) {
    if (item.url) URL.revokeObjectURL(item.url);
    setPicked((current) => current.filter((row) => row.id !== item.id));
  }

  /** Everything that has not gone anywhere yet. What is in flight stays. */
  function dropUnsent() {
    for (const row of pickedRef.current) {
      if (!row.sent && row.url) URL.revokeObjectURL(row.url);
    }
    setPicked((current) => current.filter((row) => row.sent));
  }

  useEffect(() => {
    if (!editing) return;
    setText(editing.body);
    // Editing changes words, not files: an edit goes out as its own operation
    // and has nowhere to carry these. Dropping them now beats swallowing them
    // silently on submit — but a file already on its way up keeps its tile,
    // because it is still going somewhere and can still fail.
    dropUnsent();
    inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    if (replyTo) inputRef.current?.focus();
  }, [replyTo]);

  // Grow with the text, up to a few lines, then scroll inside itself.
  useEffect(() => {
    const field = inputRef.current;
    if (!field) return;
    field.style.height = "auto";
    field.style.height = `${Math.min(field.scrollHeight, 140)}px`;
  }, [text]);

  /** Put the reason on the composer's own preview, beside the strip's. */
  function blame(item: Picked, reason: string) {
    setPicked((current) =>
      current.map((row) => (row.id === item.id ? { ...row, error: reason } : row)),
    );
  }

  /**
   * The words go first, then the bytes go to disk, and only then to the wire.
   *
   * The server refuses an attachment whose target it has never seen, and it
   * refuses it permanently — a 404, not something a retry fixes. A message
   * leaves through the outbox, so "sent" is a later moment than "typed", and
   * offline it can be a much later moment.
   *
   * That middle step is the whole of this. The files used to be held in memory
   * across the wait, so a reload — or simply closing the app on a train — lost
   * them without a word, and the message turned up later with nothing attached
   * to the caption describing it. Written to disk against the id the message was
   * minted under, they survive all of that: if this tab never comes back, the
   * queue's own drain sends them once the outbox has put the message there.
   *
   * Nothing here disappears quietly. A message that never lands takes its files
   * off the disk with it — they would hang off nothing — and every failure ends
   * up on a tile rather than in silence.
   */
  async function deliver(value: string, outgoing: Picked[]) {
    const sent = await onSend(value);

    if (!sent) {
      const stalled = new Set(outgoing.map((row) => row.id));
      setPicked((current) =>
        current.map((row) =>
          stalled.has(row.id) ? { ...row, error: t("chat.sendFailed"), sent: false } : row,
        ),
      );
      return;
    }

    const target = { type: "message", id: sent.id } as const;

    // Before the await below, not after it: from here on the pictures exist
    // somewhere other than this tab's memory.
    const kept: Picked[] = [];
    for (const item of outgoing) {
      const failure = await keep(target, item.file, { id: item.id });
      if (failure === null) kept.push(item);
      else blame(item, failure);
    }

    if (!(await sent.landed)) {
      for (const item of kept) {
        await forget(item.id);
        blame(item, t("chat.sendFailed"));
      }
      setPicked((current) =>
        current.map((row) => (row.sent ? { ...row, sent: false } : row)),
      );
      return;
    }

    // One at a time: two 10 MB photos sharing a phone's uplink finish no sooner
    // together, and each tile's percentage means something this way.
    for (const item of kept) {
      const failure = await sendKept(item.id);
      if (failure === null) {
        // Up, and in the mirror: the strip under the bubble has the real
        // thumbnail now, so the preview here has nothing left to say.
        drop(item);
      } else {
        blame(item, failure);
      }
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const value = text.trim();
    if (disabled) return;

    if (editing) {
      if (!value) return;
      onSaveEdit(value);
      setText("");
      inputRef.current?.focus();
      return;
    }

    /*
     * A photograph is a message.
     *
     * This used to insist on words, because a message carries the file and an
     * empty body was refused before the outbox — a rule about how the two rows
     * are stored, enforced on somebody who just wanted to show a picture. The
     * caption is optional now; what is still refused is a message that is
     * neither. A tile that has already been refused is not something to send.
     */
    const outgoing = picked.filter((row) => !row.error && !row.sent);
    if (!value && outgoing.length === 0) return;

    // The words leave the field the instant the button is pressed; the pictures
    // stay, because there is now something true to show on them.
    setText("");

    /*
     * And the field keeps the cursor.
     *
     * The button that sends takes focus away in order to be pressed, so on a
     * phone every «отправить» put the keyboard down — and a conversation is
     * exactly the place somebody writes the next line straight after this one.
     * The same trap the list's «Добавить» had, in the same shape.
     *
     * Inside the gesture that submitted, or the browser will refuse to raise
     * the keyboard again.
     */
    inputRef.current?.focus();
    const going = new Set(outgoing.map((row) => row.id));
    setPicked((current) =>
      current.map((row) => (going.has(row.id) ? { ...row, sent: true } : row)),
    );

    void deliver(value, outgoing);
  }

  function pick(event: ChangeEvent<HTMLInputElement>) {
    const chosen = Array.from(event.target.files ?? []);
    // Cleared before anything else, so choosing the same file twice in a row
    // still fires a change event.
    event.target.value = "";
    if (chosen.length === 0) return;

    // Built out here rather than inside the updater: an updater may be called
    // more than once for one update, and each call would mint an object URL
    // that nothing ever shows and nothing ever revokes.
    const added: Picked[] = chosen.map((file) => {
      const error = refuse(file, t);
      return {
        id: uuidv7(),
        file,
        // Only for a picture that is actually going to be drawn — a refused
        // file gets the reason instead, and a URL nothing shows is a leak.
        url: !error && file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
        error,
        sent: false,
      };
    });

    setPicked((current) => [...current, ...added]);
  }

  function unpick(item: Picked) {
    // A row the upload wrote has to go from there too, or the failure comes
    // back as a red tile under the message it was going to.
    if (item.sent) dismiss(item.id);
    drop(item);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter makes a new line — on a phone it is the only way to. Sending is the
    // button, or the shortcut people already have in their fingers.
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      submit(event as unknown as FormEvent);
    }
  }

  function cancel() {
    if (editing) onCancelEdit();
    else onCancelReply();
    setText("");
    dropUnsent();
  }

  const context = editing ?? replyTo;
  // A picture with nothing typed about it is still a message worth sending.
  const sendable = picked.some((row) => !row.error && !row.sent);

  return (
    // The composer sits on the ground rather than on a bar of its own: the field
    // is the surface, and it is what separates the writing from the reading. A
    // rule along the top would be the drawn line the direction does without.
    /* `px-4` and 44px controls, exactly as in the header: the send button then
       sits on the same vertical line as the settings gear above it, and the clip
       mirrors it on the left. Both rows are `max-w-2xl` and centred, so the two
       paddings line up rather than merely looking close. */
    <div className="shrink-0 px-4 pt-1 pb-2">
      {context ? (
        <div className="mb-1.5 flex items-center gap-2 rounded-row bg-surface px-3 py-1.5">
          {/* The same amber bar the quote inside a bubble uses, so what is being
              answered looks like one thing in both places. */}
          <span aria-hidden="true" className="h-8 w-0.5 shrink-0 rounded-pill bg-accent" />
          <div className="min-w-0 flex-1">
            <p className="text-[0.75rem] font-medium text-accent">
              {editing
                ? t("chat.editing")
                : t("chat.replyingTo", { name: context.authorName })}
            </p>
            <p className="truncate text-[0.8125rem] text-muted">{context.body}</p>
          </div>
          <button
            type="button"
            onClick={cancel}
            aria-label={editing ? t("chat.cancelEditing") : t("chat.cancelReply")}
            className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-surface-alt hover:text-ink"
          >
            <CloseIcon className="size-[18px]" strokeWidth={1.9} />
          </button>
        </div>
      ) : null}

      {/* What is about to go with the message, or is going right now.

          It scrolls sideways rather than wrapping: three 176px previews do not
          fit across a phone, and a row that wrapped into three lines would push
          the field itself off the bottom of the screen — the one thing the
          composer must never do. Two fit on screen at the smaller size, and the
          third is a thumb's flick away. */}
      {picked.length > 0 ? (
        <ul
          aria-label={t("attachments.picked")}
          className="mb-1.5 flex snap-x list-none gap-2 overflow-x-auto overscroll-x-contain px-1 pb-1"
        >
          {picked.map((item) => (
            <li key={item.id} className="shrink-0 snap-start">
              <Preview
                item={item}
                solo={picked.length === 1}
                progress={pending.find((row) => row.id === item.id)?.progress ?? 0}
                onRemove={() => unpick(item)}
              />
            </li>
          ))}
        </ul>
      ) : null}

      <form onSubmit={submit} className="flex items-end gap-2">
        {/* Hidden while editing: an edit carries words and nothing else, so
            offering the clip there would promise something it cannot do. */}
        {editing ? null : (
          <>
            <button
              type="button"
              disabled={disabled}
              onClick={() => fileRef.current?.click()}
              aria-label={t("attachments.attach")}
              className={cn(
                // The shared touch size, which is also the header's, so the row
                // reads as three objects of one height and lines up with the
                // chrome above it.
                "grid size-[var(--touch)] shrink-0 place-items-center rounded-pill transition-colors",
                "text-muted hover:bg-surface hover:text-ink disabled:text-muted/50",
              )}
            >
              <PaperclipIcon className="size-5" strokeWidth={1.9} />
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              // Wider than what is accepted, and on purpose — see the note
              // beside `ACCEPT_ATTACHMENTS`. Naming the types exactly left an
              // iPhone's camera roll greyed out, because its photographs are
              // HEIC.
              accept={ACCEPT_ATTACHMENTS}
              onChange={pick}
              className="hidden"
            />
          </>
        )}

        <textarea
          ref={inputRef}
          value={text}
          rows={1}
          disabled={disabled}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={editing ? t("chat.editMessage") : t("chat.messagePlaceholder")}
          aria-label={editing ? t("chat.editMessage") : t("chat.newMessage")}
          className={cn(
            // `min-height` outranks the inline height the grow effect writes, so
            // one line is exactly the touch size and the row stays flush with
            // the buttons either side of it. Past that the field grows upward
            // and `items-end` keeps the buttons on its last line, where the
            // thumb already is.
            "max-h-[140px] min-h-[var(--touch)] min-w-0 flex-1 resize-none rounded-[22px]",
            "bg-surface px-4 py-2.5 outline-none",
            "placeholder:text-muted disabled:text-muted",
          )}
        />
        <button
          type="submit"
          disabled={disabled || (!text.trim() && !sendable)}
          aria-label={editing ? t("common.save") : t("chat.send")}
          className={cn(
            "grid size-[var(--touch)] shrink-0 place-items-center rounded-pill transition-colors",
            "bg-accent text-ground disabled:bg-surface-alt disabled:text-muted",
          )}
        >
          {editing ? (
            <CheckIcon className="size-5" strokeWidth={1.9} />
          ) : (
            <SendIcon className="size-5" strokeWidth={1.9} />
          )}
        </button>
      </form>
    </div>
  );
}
